import { defineStore } from "pinia";
import { useMasters } from "~/store/masters";
import { useCalendar } from "~/store/calendar";
import { useUserStore } from "~/store/auth";
import { useStorage } from "@vueuse/core";
import { watch } from "vue";
import WWSS from "~/utils/wwss";

import {
  parseCalendarWOToCrews,
  parseAlgorithmWOToCrews,
  parsePlanningOTs,
} from "~/composables/workorders";

import { countAppliedFilters } from "~/composables/general";

import { formatDate, parseDateAsUTC } from "~/composables/dates";

const getWorkorderIndex = (workorders, candidate) => {
  if (!Array.isArray(workorders)) return -1;

  return workorders.findIndex((workorder) => {
    if (
      candidate?.WORKORDERID != null &&
      workorder?.WORKORDERID != null &&
      String(workorder.WORKORDERID) === String(candidate.WORKORDERID)
    ) {
      return true;
    }

    return (
      workorder?.WONUM === candidate?.WONUM &&
      workorder?.SITEID === candidate?.SITEID
    );
  });
};

const getGeneralWorkorderSortTime = (workorder) => {
  const scheduledDate = workorder?.FECHA_PROGRAMADA;
  if (!scheduledDate) return Number.POSITIVE_INFINITY;

  const parsedDate = parseDateAsUTC(scheduledDate);
  const time = parsedDate instanceof Date ? parsedDate.getTime() : Number.NaN;
  return Number.isNaN(time) ? Number.POSITIVE_INFINITY : time;
};

const sortGeneralWorkorders = (workorders) => {
  if (!Array.isArray(workorders)) return [];

  return [...workorders].sort((first, second) => {
    const dateComparison =
      getGeneralWorkorderSortTime(first) - getGeneralWorkorderSortTime(second);

    if (dateComparison !== 0) return dateComparison;

    const firstWonum = String(first?.WONUM ?? "");
    const secondWonum = String(second?.WONUM ?? "");
    return firstWonum.localeCompare(secondWonum);
  });
};

const getLeadSubcontractor = (laborId, mates) =>
  mates.find((mate) => mate.LABORID === laborId)?.SUBCONTRACTOR;

const normalizeCrewId = (value) => {
  if (value == null) return "nullish";

  const numericValue = Number(value);
  return Number.isNaN(numericValue)
    ? `string:${String(value)}`
    : `number:${numericValue}`;
};

const arrayFilterKeys = [
  "worktype",
  "crew",
  "group",
  "site",
  "asset",
  "location",
  "subcontractor",
  "planlabor",
  "gama",
  "technology",
  "location_type",
];

const algorithmFilterKeys = [
  "worktype",
  "crew",
  "group",
  "site",
  "asset",
  "location",
  "subcontractor",
  "gama",
];

const createFilterContext = (
  store,
  { filterKeys = arrayFilterKeys, includePlanlaborIndex = false } = {}
) => {
  const context = { filterSets: {} };

  for (const key of filterKeys) {
    const values = store.filters[key];
    if (Array.isArray(values) && values.length > 0) {
      context.filterSets[key] = new Set(values);
    }
  }

  const { filters } = store;
  context.woNumberQuery =
    filters.wo_number === "" || filters.wo_number === null
      ? ""
      : filters.wo_number.toLowerCase();
  context.woDescriptionQuery =
    filters.wo_description === "" || filters.wo_description === null
      ? ""
      : removeDiacritics(filters.wo_description.toLowerCase());
  context.woMotherNumberQuery =
    filters.wo_mother_number === "" || filters.wo_mother_number === null
      ? ""
      : filters.wo_mother_number.toLowerCase();

  if (filters.subcontractor.length > 0) {
    context.subcontractorByLaborId = new Map(
      useMasters().mates.list.map((mate) => [
        mate.LABORID,
        mate.SUBCONTRACTOR,
      ])
    );
  }

  if (includePlanlaborIndex && filters.planlabor.length > 0) {
    const calendar = useCalendar();
    const planlaborByWorkorder = new Map();

    for (const planlabor of store.planlaborPlanning) {
      if (
        !calendar.inCalendarNavigationWeek(
          parseDateAsUTC(planlabor.SCHEDDATE)
        )
      ) {
        continue;
      }

      const laborCodes =
        planlaborByWorkorder.get(planlabor.WORKORDERID) ?? [];
      laborCodes.push(planlabor.LABORCODE);
      planlaborByWorkorder.set(planlabor.WORKORDERID, laborCodes);
    }

    context.planlaborByWorkorder = planlaborByWorkorder;
  }

  return context;
};

const matchesArrayFilter = (filters, context, key, value) => {
  const selectedValues = filters[key];
  if (!selectedValues?.length) return true;

  const selectedSet = context.filterSets?.[key];
  return selectedSet
    ? selectedSet.has(value)
    : selectedValues.includes(value);
};

export const useGlobalScheduling = defineStore("globalScheduling", {
  state: () => ({
    lastUpdate: {
      scheduling: null,
      schedulingPlanning: null,
      saturation: null,
    },
    draggingCardWO: [], //Controlar la OT en movimiento para la replanificación
    refGrid: null,
    refGridChildrens: null,
    // Workorders  (OT) Planificadas
    planningWorkorders: [],
    filteredPlanningWorkorders: [],
    // Workorders  (OT) Algoritmo
    algorithmWorkorders: [],
    filteredAlgorithmWorkorders: [],
    algorithmLastExecution: null,
    // Workorders  (OT) Listado general derecho
    generalWorkorders: [],
    filteredGeneralWorkorders: [],
    areGeneralWorkordersUpdated: false,
    generalListFilters: {
      scheduledDate: {
        from: "",
        to: "",
      },
      targetDate: {
        from: "",
        to: "",
      },
    },
    // Plannlabor
    planlaborPlanning: [],
    crewsSlots: {},
    showCrewsData: {},
    techniciansAvailabilities: [],
    filters: {
      // Comunes
      wo_number: "",
      wo_description: "",
      worktype: [],
      crew: [],
      group: [],
      site: [],
      asset: [],
      location: [],
      subcontractor: [],
      planlabor: [],
      gama: [],
      regular: "",
      // Planificado
      wo_mother_number: "",
      technology: [],
      location_type: [],
      reprogrammable: false,
      qsew: false,
      loading: {},
      // Datos filtrables obtenidos desde el listado de cuadrillas
      data: {
        location_type: {},
        gama: {},
        // Regular: están invertidos. FDoble negación FLG_NOT_REGULAR
        regular: [
          { title: "common.buttons.none", value: "" },
          { title: "common.buttons.yes", value: "0" },
          { title: "common.buttons.no", value: "1" },
        ],
      },
      isFiltered: false,
      layaouts: [],
    },
    filterTemplates: [],
    updatingFilters: false,
    filterApplied: null,
    hasPendingFilters: false,
    exportingExcel: false,
    defaultRightPanel: 1, // Panel derecho: 1 = listado OTs general, 2 = calendario algoritmo
  }),
  actions: {
    toggleRightPanel(panelNumber) {
      this.defaultRightPanel = panelNumber;
    },
    setRightPanel(value) {
      this.defaultRightPanel = value;
    },
    setPendingFilters(value = true) {
      this.hasPendingFilters = value;
    },
    setGamaFilterData(gama_plan) {
      if (!gama_plan.CODE) return;

      const storedGama = this.filters.data.gama[gama_plan.CODE] || {};

      // Actualiza solo las descripciones que existan en el nuevo dato y estén vacías en el almacenamiento
      this.filters.data.gama[gama_plan.CODE] = {
        DESCRIPTION_ES:
          storedGama.DESCRIPTION_ES || gama_plan.DESCRIPTION_ES || "",
        DESCRIPTION_EN:
          storedGama.DESCRIPTION_EN || gama_plan.DESCRIPTION_EN || "",
      };
    },
    setLocationTypeFilterData(location_type) {
      const masters = useMasters();

      if (location_type.CODE == null) {
        return;
      }
      const storedLocation =
        this.filters.data.location_type[location_type.CODE];

      const location_type_data = masters.locTypes.list.find(
        (lt) => lt.VALUE === location_type.CODE
      );

      if (location_type_data && !storedLocation) {
        this.filters.data.location_type[location_type.CODE] = {
          DESCRIPTION_ES: location_type_data.DESCRIPTION_ES || "",
          DESCRIPTION_EN: location_type_data.DESCRIPTION_EN || "",
        };
      } else {
        if (
          !storedLocation.DESCRIPTION_ES &&
          location_type_data.DESCRIPTION_ES
        ) {
          storedLocation.DESCRIPTION_ES = location_type_data.DESCRIPTION_ES;
        }
        if (
          !storedLocation.DESCRIPTION_EN &&
          location_type_data.DESCRIPTION_EN
        ) {
          storedLocation.DESCRIPTION_EN = location_type_data.DESCRIPTION_EN;
        }
      }
    },
    setCrewsSlots(value) {
      this.crewsSlots = value;
    },

    resetGeneralListFilters() {
      this.generalListFilters = {
        scheduledDate: {
          from: "",
          to: "",
        },
        targetDate: {
          from: "",
          to: "",
        },
      };
    },

    updateGeneralListFilter(filterKey, value = {}) {
      const current = this.generalListFilters[filterKey];
      if (!current) return;

      this.generalListFilters[filterKey] = {
        ...current,
        ...value,
      };
    },

    getWorkorderTargetDate(wo) {
      return (
        wo?.FECHA_OBJETIVO ||
        wo?.FECHAOBJETIVO ||
        wo?.PMNEXTDATE ||
        wo?.TARGET_DATE ||
        wo?.TARGETDATE ||
        wo?.OBJETIVO_FECHA ||
        ""
      );
    },

    matchesGeneralListDateRange(dateValue, range = {}) {
      if (!range?.from && !range?.to) return true;
      if (!dateValue) return false;

      const normalizedDate = formatDate(parseDateAsUTC(dateValue), "YMD", "-");
      const from = range.from || "";
      const to = range.to || "";

      if (from && normalizedDate < from) return false;
      if (to && normalizedDate > to) return false;

      return true;
    },

    matchesGeneralListFilters(wo) {
      return (
        this.matchesGeneralListDateRange(
          wo?.FECHA_PROGRAMADA,
          this.generalListFilters.scheduledDate
        ) &&
        this.matchesGeneralListDateRange(
          this.getWorkorderTargetDate(wo),
          this.generalListFilters.targetDate
        )
      );
    },

    matchesPlanningFilters(wo, options = {}) {
      const { includePlanlabor = true, filterContext = {} } = options;

      const woNumberQuery =
        filterContext.woNumberQuery ??
        (this.filters.wo_number === "" || this.filters.wo_number === null
          ? ""
          : this.filters.wo_number.toLowerCase());
      if (
        woNumberQuery &&
        !wo.WONUM.toLowerCase().includes(woNumberQuery)
      ) {
        return false;
      }

      if (
        !matchesArrayFilter(
          this.filters,
          filterContext,
          "worktype",
          wo.CLASE_TRABAJO
        ) ||
        !matchesArrayFilter(
          this.filters,
          filterContext,
          "group",
          wo.PERSONGROUP
        ) ||
        !matchesArrayFilter(
          this.filters,
          filterContext,
          "crew",
          wo.AMCREW
        ) ||
        !matchesArrayFilter(
          this.filters,
          filterContext,
          "site",
          wo.SITEID
        )
      ) {
        return false;
      }

      if (
        this.filters.asset.length > 0 &&
        !matchesArrayFilter(
          this.filters,
          filterContext,
          "asset",
          `${wo.SITEID}-${wo.LOCATION.split(".")[0]}`
        )
      ) {
        return false;
      }

      if (
        this.filters.location.length > 0 &&
        !matchesArrayFilter(
          this.filters,
          filterContext,
          "location",
          `${wo.SITEID}-${wo.LOCATION}`
        )
      ) {
        return false;
      }

      if (this.filters.subcontractor.length > 0) {
        const leadSubcontractor = filterContext.subcontractorByLaborId
          ? filterContext.subcontractorByLaborId.get(wo.ENTREGA)
          : getLeadSubcontractor(wo.ENTREGA, useMasters().mates.list);
        if (
          !matchesArrayFilter(
            this.filters,
            filterContext,
            "subcontractor",
            leadSubcontractor
          )
        ) {
          return false;
        }
      }

      if (includePlanlabor && this.filters.planlabor.length > 0) {
        const selectedLaborCodes = filterContext.filterSets?.planlabor;
        const matchesLabor = (laborCode) =>
          selectedLaborCodes
            ? selectedLaborCodes.has(laborCode)
            : this.filters.planlabor.includes(laborCode);

        if (filterContext.planlaborByWorkorder) {
          const laborCodes =
            filterContext.planlaborByWorkorder.get(wo.WORKORDERID) ?? [];
          if (!laborCodes.some(matchesLabor)) return false;
        } else {
          const weekPlanlabor = this.getWeekPlanlabor(wo.WORKORDERID);
          if (
            !weekPlanlabor.some((planlabor) =>
              matchesLabor(planlabor.LABORCODE)
            )
          ) {
            return false;
          }
        }
      }

      if (
        !matchesArrayFilter(
          this.filters,
          filterContext,
          "gama",
          wo.GAMA_PLAN
        ) ||
        !matchesArrayFilter(
          this.filters,
          filterContext,
          "technology",
          wo.TECNOLOGIA
        ) ||
        !matchesArrayFilter(
          this.filters,
          filterContext,
          "location_type",
          wo.TIPO_LOCATION
        ) ||
        (this.filters.regular !== "" &&
          !this.filters.regular.includes(wo.FLG_NOTREGULAR)) ||
        (this.filters.reprogrammable && wo.OTAREPROGRAMAR != "1") ||
        (this.filters.qsew && wo.QSEW != "1")
      ) {
        return false;
      }

      const woMotherNumberQuery =
        filterContext.woMotherNumberQuery ??
        (this.filters.wo_mother_number === "" ||
        this.filters.wo_mother_number === null
          ? ""
          : this.filters.wo_mother_number.toLowerCase());
      if (
        woMotherNumberQuery &&
        (wo.OTMADRE == null ||
          !wo.OTMADRE.toLowerCase().includes(woMotherNumberQuery))
      ) {
        return false;
      }

      const woDescriptionQuery =
        filterContext.woDescriptionQuery ??
        (this.filters.wo_description === "" ||
        this.filters.wo_description === null
          ? ""
          : removeDiacritics(this.filters.wo_description.toLowerCase()));
      return (
        !woDescriptionQuery ||
        removeDiacritics(wo.DESCRIPTION.toLowerCase()).includes(
          woDescriptionQuery
        )
      );
    },



    setPlanningWorkorders(value) {
      this.planningWorkorders = value;
      this.setFilteredAlgorithmWorkorders();
    },
    setAlgorithmWorkorders(value) {
      this.algorithmWorkorders = value;
    },
    setGeneralWorkorders(value) {
      this.generalWorkorders = sortGeneralWorkorders(value);
      this.areGeneralWorkordersUpdated = true;
      this.setFilteredAlgorithmWorkorders();
    },
    setModyfingCardWO(value) {
      this.draggingCardWO.push(value);
    },
    clearDraggingCardWO() {
      this.draggingCardWO = [];
    },
    removeDraggingCardWO(wonum) {
      this.draggingCardWO = this.draggingCardWO.filter((wo) => wo !== wonum);
    },
    
    
    async setFilteredPlanningWorkorders(changedCrews = []) {
      const filterContext = createFilterContext(this, {
        includePlanlaborIndex: true,
      });
      // Si no hay cuadrillas cambiadas, procesamos todas las órdenes de trabajo
      const workorders_filtered = this.planningWorkorders.filter((wo) => {
        // Solo filtramos si el AMCREW de la orden de trabajo está en las cuadrillas afectadas o si no hay cuadrillas afectadas
        const inCrewsUpdated =
          (changedCrews.length > 0 && !changedCrews.includes(wo.AMCREW)) ||
          true;

        return (
          inCrewsUpdated &&
          this.matchesPlanningFilters(wo, { filterContext })
        );
      });

      if (
        workorders_filtered.length < 1 &&
        countAppliedFilters(this.filters) > 0
      ) {
        this.filteredPlanningWorkorders = [];
      } else {
        // Actualizamos solo los elementos que se han filtrado
        this.filteredPlanningWorkorders =
          parseCalendarWOToCrews(workorders_filtered);
      }
    },
    setFilteredGeneralWorkorders() {
      const filterContext = createFilterContext(this, {
        includePlanlaborIndex: true,
      });
      this.filteredGeneralWorkorders = this.generalWorkorders.filter(
        (wo) => this.matchesPlanningFilters(wo, { filterContext })
      );
    },

    setFilteredPlanningCrew(crew) {

      console.log("Rellamando a setFilteredPlanningCrew para la cuadrilla:", crew.AMCREW);

      const crew_data = this.filteredPlanningWorkorders.find((c) => {
        return c.id === crew.AMCREW;
      });
      const slot = crew_data.slots.find((s) => {
        return s.AMCREWAVAILAEID === crew.AMCREWAVAILAEID;
      });
      slot.workers =
        crew.LABORCODE_AMCREWLABORAEID == "" ||
        crew.LABORCODE_AMCREWLABORAEID == null
          ? []
          : crew.LABORCODE_AMCREWLABORAEID.split(",").map((worker) =>
              worker.replace(/_.*/, "")
            );
      crew_data.ots = [];

      //OTS
      const crew_ots = this.planningWorkorders.filter((wo_data) => {
        return wo_data.AMCREW == crew.AMCREW;
      });
      const { wo, unplanned_wo } = parsePlanningOTs(crew_ots, crew_data);
      crew_data.ots = wo;
      crew_data.unplanned_ots = unplanned_wo;
    },
    setFilteredAlgorithmWorkorders() {
      function getAsset(text) {
        const regex = /-(\d+)\./;
        const match = text.match(regex);
        return match ? match[1] : null;
      }

      if (!this.algorithmWorkorders || this.algorithmWorkorders.length == 0) {
        this.filteredAlgorithmWorkorders = [];
        return;
      }
      const filterContext = createFilterContext(this, {
        filterKeys: algorithmFilterKeys,
      });
      const crewById =
        this.filters.crew.length > 0
          ? new Map(
              useMasters().crews.list.map((crew) => [
                normalizeCrewId(crew.AMCREWID),
                crew,
              ])
            )
          : null;
      const workordersByCode =
        this.filters.subcontractor.length > 0
        ? new Map(
            [...this.generalWorkorders, ...this.planningWorkorders].map(
              (workorder) => [
                `${workorder.SITEID}-${workorder.WONUM}`,
                workorder,
              ]
            )
          )
        : null;
      const matchesFilter = (key, value) =>
        matchesArrayFilter(this.filters, filterContext, key, value);

      const workorders_filtered = this.algorithmWorkorders.filter((wo_data) => {
        if (!matchesFilter("worktype", wo_data.COD_WORKTYPE)) return false;
        if (!matchesFilter("group", wo_data.COD_PERSONGROUP)) return false;

        if (crewById) {
        const crew = crewById.get(normalizeCrewId(wo_data.OPT_CREW));
        if (!matchesFilter("crew", crew?.AMCREW)) return false;
        }

        if (
        this.filters.site.length > 0 &&
        !matchesFilter("site", wo_data.COD_LOCATION_ID.split("-")[0])
        ) {
        return false;
        }

        if (workordersByCode) {
        const relatedWorkorder = workordersByCode.get(
          wo_data.COD_WONUM_ID
        );
        const leadSubcontractor =
          filterContext.subcontractorByLaborId?.get(
            wo_data.ENTREGA || relatedWorkorder?.ENTREGA
          );
        if (!matchesFilter("subcontractor", leadSubcontractor)) {
          return false;
        }
        }

        if (
        this.filters.asset.length > 0 &&
        !matchesFilter("asset", wo_data.COD_LOCATION_ID.split(".")[0])
        ) {
        return false;
        }
        if (
        this.filters.location.length > 0 &&
        !matchesFilter("location", wo_data.COD_LOCATION_ID)
        ) {
        return false;
        }
        if (!matchesFilter("gama", wo_data.COD_GAMA)) return false;

        const woRegular = wo_data.FLG_NOTREGULAR ? "1" : "0";
        if (
        this.filters.regular !== "" &&
        !this.filters.regular.includes(woRegular)
        ) {
        return false;
        }

        if (filterContext.woNumberQuery) {
        const wonum = wo_data.COD_WONUM_ID.split("-")[1];
        if (!wonum.toLowerCase().includes(filterContext.woNumberQuery)) {
          return false;
        }
        }

        return (
        !filterContext.woDescriptionQuery ||
        removeDiacritics(wo_data.DES_DESCRIPTION.toLowerCase()).includes(
          filterContext.woDescriptionQuery
        )
        );
      });

      this.filteredAlgorithmWorkorders =
        parseAlgorithmWOToCrews(workorders_filtered);
    },
    async saveCurrentFilters() {
      // Guardar SIEMPRE la selección actual (aunque esté vacía).
      // Si el usuario limpia el filtro y no actualizamos `loading.*`,
      // en el siguiente refresco `setWorkordersFilters()` re-aplicará
      // la selección anterior desde `loading.*`.
      this.filters.loading.gama = [...(this.filters.gama ?? [])];
      this.filters.loading.location_type = [
        ...(this.filters.location_type ?? []),
      ];

      await this.clearFiltersData();
    },
    async clearFiltersData() {
      if (this.filters.data.gama) {
        this.filters.data.gama = {};
      }

      // Las siguientes líneas se comentaron porque provocaban que el filtro de gama quedase vacío al cambiar de semana
      // a través de la navegación de calendario. Esto llegaba a provocar una mala identificación del filtro aplicado.
      // this.filters.gama = [];
      // this.filters.data.location_type = {};
      // this.filters.location_type = [];
    },

    updateGamaFilterData(ot, key) {
      if (!key || !ot[key]) return;

      let gamaPlan = {
        CODE: ot[key],
        DESCRIPTION_ES:
          (key === "GAMA_PLAN" && ot.GAMA_PLAN_DESCRIPTION_ES) || null,
        DESCRIPTION_EN:
          (key === "GAMA_PLAN" && ot.GAMA_PLAN_DESCRIPTION_EN) || null,
      };

      this.setGamaFilterData(gamaPlan);
    },

    updateLocationTypeFilterData(ot) {
      const locationType = {
        CODE: ot.TIPO_LOCATION,
        DESCRIPTION_ES: null,
        DESCRIPTION_EN: null,
      };
      this.setLocationTypeFilterData(locationType);
    },

    /** Añade el filtro si se encuentra entre los filtros alimentados por OTS
     * @param {String} filterKey // clave del filtro
     * @param {Array}  filteredItems //Valores filtrados anteriormente
     */
    updateFilters(filterKey, filteredItems) {
      filteredItems.forEach((item) => {
        if (
          this.filters.data[filterKey][item] &&
          !this.filters[filterKey].includes(item)
        ) {
          this.filters[filterKey].push(item);
        }
      });
    },

    async setWorkordersFilters() {
      // Recorrer las OTs para actualizar los filtros alimentados por OTs
      this.planningWorkorders.forEach((ot) => {
        this.updateGamaFilterData(ot, "GAMA_PLAN");
        this.updateLocationTypeFilterData(ot);
      });

      this.generalWorkorders.forEach((ot) => {
        this.updateGamaFilterData(ot, "GAMA_PLAN");
        this.updateLocationTypeFilterData(ot);
      });

      this.algorithmWorkorders.forEach((ot) => {
        this.updateGamaFilterData(ot, "COD_GAMA");
      });

      // Volver a seleccionar los filtros seleccionados antes de la actualziación
      const gama_filtered = this.filters.loading.gama ?? [];
      const location_type_filtered = this.filters.loading.location_type ?? [];
      this.updateFilters("gama", gama_filtered);
      this.updateFilters("location_type", location_type_filtered);

      // this.filters.loading = {};
    },
    async cleanWorkorders(includeGeneralWorkorders = true) {
      // Limpiar OTs
      this.algorithmWorkorders = [];
      this.planningWorkorders = [];
      // Limpiado  OTs filtradas
      this.filteredAlgorithmWorkorders = [];
      this.filteredPlanningWorkorders = [];

      if (includeGeneralWorkorders) {
        this.generalWorkorders = [];
        this.filteredGeneralWorkorders = [];
      }
    },
    setShowCrewsData(crews) {
      for (const crew of crews) {
        this.showCrewsData[crew.AMCREW] = {
          planning: false,
          algorithm: false,
        };
      }
      this.showCrewsData[CREW_NULL] = {
        planning: false,
        algorithm: false,
      };
    },
    toggleShowCrewData(AMCREW, calendar) {
      this.showCrewsData[AMCREW][calendar] =
        !this.showCrewsData[AMCREW][calendar];
    },
    async updateWorkorder(new_data) {
      const calendar = useCalendar();
      const hasWeekPlanlabor = this.getWeekPlanlabor(new_data.WORKORDERID).length > 0;
      const shouldKeepInPlanningWeek =
        calendar.inCalendarNavigationWeek(
          parseDateAsUTC(new_data.FECHA_PROGRAMADA)
        ) || hasWeekPlanlabor;

      const wo_index = this.planningWorkorders.findIndex(
        (wo) => wo.WONUM === new_data.WONUM && wo.SITEID === new_data.SITEID
      );
      const general_index = getWorkorderIndex(this.generalWorkorders, new_data);

      let previous_crewId = null;
      let changedCrews = [new_data.AMCREW || CREW_NULL]; // añado la nueva cuadrilla al array

      if (wo_index >= 0) {
        if (
          this.planningWorkorders[wo_index].FECHA_PROGRAMADA !==
            new_data.FECHA_PROGRAMADA &&
          this.workorderHasPlanlabor(new_data.WORKORDERID)
        ) {
          this.removeWOPlanlaborFromList(new_data.WORKORDERID);
        }

        previous_crewId = this.planningWorkorders[wo_index].AMCREW || CREW_NULL;
        if (new_data.AMCREW !== previous_crewId) {
          changedCrews.push(previous_crewId);
        }
        this.planningWorkorders.splice(wo_index, 1);
      }

      if (shouldKeepInPlanningWeek) {
        this.planningWorkorders.push(new_data);
      }

      if (general_index >= 0) {
        this.generalWorkorders.splice(general_index, 1, new_data);
      } else {
        this.generalWorkorders.push(new_data);
      }

      this.generalWorkorders = sortGeneralWorkorders(this.generalWorkorders);

      this.setFilteredGeneralWorkorders();

      return changedCrews;
    },
    setPlanlaborPlanning(data) {
      this.planlaborPlanning = data;
      this.setFilteredPlanningWorkorders();
    },
    setTechniciansAvailabilities(data) {
      this.techniciansAvailabilities = data;
    },
    async setWorkorderPlanlabor(workorderIds) {
      try {
        const results = await Promise.all(
          workorderIds.map((id) =>
            fetchPostData(WWSS.scheduling.planlabor.get_planlabor_wo, {
              workorderid: id,
            }).catch((reason) => {
              console.error(
                "Error al obtener la mano de obra planificada de la OT",
                id,
                reason
              );
              return null; // así el Promise.all no falla y podemos filtrar después
            })
          )
        );
        // Filtrar nulos y vacíos
        const laborData = results.filter((r) => r);

        // Si cada petición devuelve un array:
        this.planlaborPlanning = this.planlaborPlanning.concat(...laborData);
      } catch (error) {
        console.error(
          "Error al obtener la mano de obra planificada de la OT",
          id,
          error
        );
      }
    },
    async refreshWorkordersBatch(items = []) {
      const calendar = useCalendar();
      const normalizedItems = Array.isArray(items)
        ? items.filter((item) => item?.workorder?.WORKORDERID)
        : [];

      if (!normalizedItems.length) return;

      const refreshedPlanlaborIds = new Set();

      for (const item of normalizedItems) {
        const { workorder, syncPlanlabor = true } = item;
        const workorderid = workorder.WORKORDERID;
        const hadWeekPlanlabor = this.getWeekPlanlabor(workorderid).length > 0;

        if (syncPlanlabor) {
          this.removeWOPlanlaborFromList(workorderid);

          if (
            calendar.inCalendarNavigationWeek(
              parseDateAsUTC(workorder.FECHA_PROGRAMADA)
            ) || hadWeekPlanlabor
          ) {
            refreshedPlanlaborIds.add(workorderid);
          }
        }

        await this.updateWorkorder(workorder);

        this.updateGamaFilterData(workorder, "GAMA_PLAN");
        this.updateLocationTypeFilterData(workorder);
      }

      if (refreshedPlanlaborIds.size) {
        await this.setWorkorderPlanlabor([...refreshedPlanlaborIds]);
      }

      await this.setFilteredPlanningWorkorders();
      this.setFilteredGeneralWorkorders();
    },
    removeWOPlanlaborFromList(workorderid) {
      this.planlaborPlanning = this.planlaborPlanning.filter(
        (pl) => pl.WORKORDERID !== workorderid
      );
    },
    removePlanlaborFromList(planlaborData) {
      this.planlaborPlanning = this.planlaborPlanning.filter(
        (pl) =>
          !(
            pl.WORKORDERID === planlaborData.WORKORDERID &&
            pl.LABORCODE === planlaborData.LABORCODE &&
            pl.SCHEDDATE === planlaborData.SCHEDDATE
          )
      );
    },
    async initFilters() {
      const userStore = useUserStore();
      const useremail = userStore.getUser.username;
      const username = userStore.login?.CodOperarioMaximo;

      let filterTemplates = await fetchPostData(WWSS.filters.get, {
        favfilter_user: username,
      });

      if (!Array.isArray(filterTemplates)) {
        filterTemplates = [];
      } else {
        this.filterTemplates = filterTemplates;
      }

      // Si ya hay un filtro aplicado, no cargar el filtro por defecto
      if (this.filterApplied || this.filterApplied === "") {
        return;
      }

      // Verificar si es el primer login (globalScheduling:filters no existe en localStorage)
      const isFirstLogin = !localStorage.getItem("globalScheduling:filters");

      // Sincronizar los filtros con el almacenamiento local
      const storedFilters = useStorage(
        "globalScheduling:filters",
        this.filters
      );

      if (isFirstLogin) {
        // Coger el filtro por defecto y aplicarlo
        const defaultFilter = this.filterTemplates.find(
          (f) => f.favfilter_default
        );
        if (defaultFilter) {
          this.setFilters(defaultFilter.filters);
          this.filterApplied = defaultFilter;
          return;
        }
      } else {
        // console.log("No es el primer login, se cargarán filtros desde localStorage...");
      }

      // Observar cambios en `filters` y actualizar `localStorage`
      watch(
        () => this.filters,
        (newFilters) => {
          storedFilters.value = newFilters;
        },
        { deep: true } // Necesario para detectar cambios en propiedades anidadas
      );

      // Comprobar si el filtro almacenado tiene contenido; en tal caso, aplicarlo
      if (countAppliedFilters(storedFilters.value) > 0) {
        this.setFilters(storedFilters.value);
      }
    },
    setFilters(newFilters = {}) {
      try {
        let loadedFiltersData;

        if (typeof newFilters === "string") {
          loadedFiltersData = JSON.parse(newFilters);
        } else {
          loadedFiltersData = newFilters;
        }

        // 1. MANTENER las propiedades de estado y temporales actuales
        // (Por ejemplo: loading, data, isFiltered, layaouts)
        const currentFiltersState = {
          loading: this.filters.loading,
          data: this.filters.data,
          isFiltered: this.filters.isFiltered,
          layaouts: this.filters.layaouts,
        };

        // Forma 2: Si 'this.filters' se reemplaza directamente (Vue 3 Ref/State)
        this.filters = {
          ...currentFiltersState, // Primero el estado para asegurar que se mantiene por defecto
          ...loadedFiltersData, // Luego los datos cargados para sobrescribir los campos de filtro
        };

        this.hasPendingFilters = false;

        return true;
      } catch (e) {
        console.error("Error al cargar y parsear filtros:", e);
        this.filters = {}; // Fallback seguro
        return false;
      }
    },
    setLastUpdate(page) {
      if (this.lastUpdate.hasOwnProperty(page)) {
        this.lastUpdate[page] = parseDateAsUTC();
      }
    },
  },
  getters: {
    getLastUpdate(state) {
      return (page) => state.lastUpdate[page] || null;
    },
    slotAvailable(state) {
      return (date, crew_id) => {
        return !!state.crewsSlots[crew_id]?.find(
          (s) => s.OPDATE == formatDate(date, "YMD", "-")
        );
      };
    },
    getWorkorderid(state) {
      return (wonum, siteid) => {
        let foundWorkorder = state.generalWorkorders.find(
          (w) => w.WONUM === wonum && w.SITEID === siteid
        );

        // Si no se encuentra, busca en planningWorkorders
        if (!foundWorkorder) {
          foundWorkorder = state.planningWorkorders.find(
            (w) => w.WONUM === wonum && w.SITEID === siteid
          );
        }

        // Devuelve el WORKORDERID o null si no se encontró
        return foundWorkorder ? foundWorkorder.WORKORDERID : null;
      };
    },
    getWorkorderPlanlabor: (state) => (workorderid) =>
      state.planlaborPlanning.filter(
        (planlabor) => planlabor.WORKORDERID === workorderid
      ),
    workorderHasPlanlabor: (state) => (workorderid) =>
      state.planlaborPlanning.some(
        (planlabor) => planlabor.WORKORDERID === workorderid
      ),
    workordersHaveWeekPlanlabor: (state) => (workorderids) =>
      workorderids.some((workorderid) => {
        const workorderPlanlabor = state.getWeekPlanlabor(workorderid);
        // Filtramos si alguna de la mano de obra planificada de la semana de hoy en adelante
        return (
          workorderPlanlabor.length > 0 &&
          workorderPlanlabor.some((p) =>
            compareDateWithToday(parseDateAsUTC(p.SCHEDDATE), ">=")
          )
        );
      }),
    planlaborInNavigationWeek: (state) => (schedDate) => {
      return useCalendar().inCalendarNavigationWeek(parseDateAsUTC(schedDate));
    },
    planlaborBeforeNavigationWeek: (state) => (schedDate) => {
      const date = normalizeDate(parseDateAsUTC(schedDate));
      const weekStart = parseDateAsUTC(useCalendar().currentWeekStartDate);

      return date < weekStart;
    },
    planlaborAfterNavigationWeek: (state) => (schedDate) => {
      const date = normalizeDate(parseDateAsUTC(schedDate));
      const weekEnd = parseDateAsUTC(useCalendar().currentWeekEndDate);

      return weekEnd < date;
    },
    getWeekPlanlaborHours: (state) => (workorderid) =>
      state
        .getWeekPlanlabor(workorderid)
        .reduce((total, entry) => total + Number(entry.LABORHRS || 0), 0),
    getWeekPlanlabor: (state) => (workorderid) =>
      state.planlaborPlanning.filter(
        (planlabor) =>
          planlabor.WORKORDERID === workorderid &&
          useCalendar().inCalendarNavigationWeek(
            parseDateAsUTC(planlabor.SCHEDDATE)
          )
      ),
    calendarNavigationPlanlabor: (state) => {
      const { inCalendarNavigationWeek } = useCalendar();

      return state.planlaborPlanning.filter((planlabor) =>
        inCalendarNavigationWeek(parseDateAsUTC(planlabor.SCHEDDATE))
      );
    },
    getWorkorderWonum: (state) => (workorderid) => {
      return state.planningWorkorders.find(
        (workorder) => workorder.WORKORDERID === workorderid
      )?.WONUM;
    },
  },
});
